import React, { useEffect, useState, useRef } from 'react';
import { useTour } from '../../context/TourContext';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface SpotlightStyle {
  top: number;
  left: number;
  width: number;
  height: number;
}

export default function TourOverlay() {
  const { isOpen, currentStepIndex, totalSteps, currentStep, endTour, nextStep, prevStep } = useTour();
  const [spotlightStyle, setSpotlightStyle] = useState<SpotlightStyle | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const retryIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);

  // Find element with retry logic
  useEffect(() => {
    if (!isOpen || !currentStep?.id) {
      setSpotlightStyle(null);
      setTooltipPosition(null);
      return;
    }

    console.log('Tour: Looking for ID:', currentStep.id);

    // Clear any existing retry interval
    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
      retryIntervalRef.current = null;
    }
    retryCountRef.current = 0;

    const findAndHighlightElement = () => {
      const element = document.getElementById(currentStep.id);
      
      if (!element) {
        console.warn(`Tour: Element not found for ID: ${currentStep.id} (attempt ${retryCountRef.current + 1}/20)`);
        retryCountRef.current++;
        
        // Retry up to 20 times (4 seconds total for lazy-loaded components)
        if (retryCountRef.current >= 20) {
          console.error(`Tour: Giving up on ID: ${currentStep.id} after 20 attempts`);
          if (retryIntervalRef.current) {
            clearInterval(retryIntervalRef.current);
            retryIntervalRef.current = null;
          }
          // Show tooltip in center even if element not found
          setSpotlightStyle(null);
          setTooltipPosition({
            top: window.innerHeight / 2 - 100,
            left: window.innerWidth / 2 - 200
          });
        }
        return;
      }

      // Element found! Clear retry interval
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }

      console.log('Tour: Element found!', element);

      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });

      // Wait for scroll to complete, then calculate position
      setTimeout(() => {
        const rect = element.getBoundingClientRect();
        
        // Calculate absolute position including scroll
        const spotlight: SpotlightStyle = {
          top: rect.top + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width,
          height: rect.height
        };

        setSpotlightStyle(spotlight);

        // Calculate tooltip position
        const padding = 24;
        const extraSpace = 200; // Extra space to avoid dropdowns/overlaps
        const tooltipWidth = 400;
        const tooltipHeight = 150;

        let tooltipTop = 0;
        let tooltipLeft = 0;

        switch (currentStep.position) {
          case 'top':
            tooltipTop = rect.top - tooltipHeight - padding;
            tooltipLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
            break;
          case 'bottom':
            tooltipTop = rect.bottom + padding + extraSpace;
            tooltipLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
            break;
          case 'left':
            tooltipTop = rect.top + rect.height / 2 - tooltipHeight / 2;
            tooltipLeft = rect.left - tooltipWidth - padding;
            break;
          case 'right':
            tooltipTop = rect.top + rect.height / 2 - tooltipHeight / 2;
            tooltipLeft = rect.right + padding;
            break;
          case 'center':
          default:
            tooltipTop = window.innerHeight / 2 - tooltipHeight / 2;
            tooltipLeft = window.innerWidth / 2 - tooltipWidth / 2;
            break;
        }

        // Keep tooltip on screen
        tooltipTop = Math.max(padding, Math.min(tooltipTop, window.innerHeight - tooltipHeight - padding));
        tooltipLeft = Math.max(padding, Math.min(tooltipLeft, window.innerWidth - tooltipWidth - padding));

        if (currentStep.offset) {
          tooltipTop += currentStep.offset.y;
          tooltipLeft += currentStep.offset.x;
        }

        setTooltipPosition({ top: tooltipTop, left: tooltipLeft });
        
        // Clear any text selection that might have occurred during scrolling
        // Do this at the end after all positioning is complete
        setTimeout(() => {
          if (window.getSelection) {
            const selection = window.getSelection();
            if (selection) {
              selection.removeAllRanges();
            }
          }
        }, 50);
      }, 300); // Wait for scroll animation
    };

    // Try immediately
    findAndHighlightElement();

    // If element not found, retry every 200ms (total 4 seconds for lazy components)
    if (!document.getElementById(currentStep.id)) {
      retryIntervalRef.current = setInterval(findAndHighlightElement, 200);
    }

    // Cleanup
    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
    };
  }, [isOpen, currentStep]);

  // Handle window resize
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => {
      // Re-trigger element finding
      if (currentStep?.id) {
        const element = document.getElementById(currentStep.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          setSpotlightStyle({
            top: rect.top + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
            height: rect.height
          });
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, currentStep]);

  // Reset drag offset when step changes
  useEffect(() => {
    setDragOffset({ x: 0, y: 0 });
    setIsDragging(false);
  }, [currentStepIndex]);

  // Drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - (tooltipPosition?.left || 0),
      y: e.clientY - (tooltipPosition?.top || 0)
    });
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (tooltipPosition) {
        setTooltipPosition({
          top: e.clientY - dragOffset.y,
          left: e.clientX - dragOffset.x
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, tooltipPosition]);

  if (!isOpen || !currentStep) return null;

  return (
    <>
      {/* Overlay with cutout using SVG mask */}
      {spotlightStyle && (
        <svg
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 9997,
            pointerEvents: 'none',
          }}
        >
          <defs>
            <mask id="tour-spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              <rect
                x={spotlightStyle.left - 8}
                y={spotlightStyle.top - 8}
                width={spotlightStyle.width + 16}
                height={spotlightStyle.height + 16}
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.3)"
            mask="url(#tour-spotlight-mask)"
          />
        </svg>
      )}
      
      {/* Spotlight Border */}
      {spotlightStyle && (
        <div
          style={{
            position: 'absolute',
            top: `${spotlightStyle.top - 8}px`,
            left: `${spotlightStyle.left - 8}px`,
            width: `${spotlightStyle.width + 16}px`,
            height: `${spotlightStyle.height + 16}px`,
            border: '4px solid #3b82f6',
            borderRadius: '8px',
            zIndex: 9998,
            pointerEvents: 'none',
            transition: 'all 0.3s ease',
          }}
        />
      )}

      {/* Tooltip */}
      {tooltipPosition && (
        <div
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            width: '400px',
            maxWidth: 'calc(100vw - 40px)',
            zIndex: 9999,
            pointerEvents: 'auto',
          }}
          className="bg-white rounded-xl shadow-2xl border-2 border-blue-500"
        >
          {/* Draggable Header */}
          <div
            onMouseDown={handleMouseDown}
            className="px-6 pt-6 pb-3 cursor-move select-none"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            {/* Close Button */}
            <button
              onClick={endTour}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors z-10"
              aria-label="Close tour"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <X size={20} />
            </button>

            {/* Step Counter */}
            <div className="text-xs font-semibold text-blue-600 mb-1">
              Step {currentStepIndex + 1} of {totalSteps}
            </div>
            <div className="text-[10px] text-gray-400">Click and drag to move</div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <p className="text-gray-800 text-base leading-relaxed mb-6">
              {currentStep.text}
            </p>

            {/* Navigation */}
            <div className="flex items-center justify-between">
            <button
              onClick={prevStep}
              disabled={currentStepIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
              Previous
            </button>

            <div className="flex gap-1">
              {Array.from({ length: totalSteps }, (_, i) => (
                <div
                  key={i}
                  className={`h-2 rounded-full transition-all ${
                    i === currentStepIndex
                      ? 'w-6 bg-blue-600'
                      : i < currentStepIndex
                      ? 'w-2 bg-blue-400'
                      : 'w-2 bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextStep}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {currentStepIndex === totalSteps - 1 ? 'Finish' : 'Next'}
              {currentStepIndex < totalSteps - 1 && <ChevronRight size={16} />}
            </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
